<script setup lang="ts">
import { watch, ref } from 'vue'

import { getClasses } from '~/js/composables/_fiftyfiveplus/classes'
import { getIsMobile } from '~/js/composables/_fiftyfiveplus/device'
import { getGlobalTranslation } from '~/js/composables/_fiftyfiveplus/translations'

import { Video } from '~/js/interfaces/_fiftyfiveplus/media'

import ButtonControls from '~/vue/__fiftyfiveplus/_atoms/buttons/ButtonControls.vue'

export interface Props {
    video: Video,
    utilities?: string
    reset?: boolean
    rounded?: boolean
    controls?: boolean
    autoplay?: boolean
    mute?: boolean
    loop?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    utilities: '',
    reset: false,
    rounded: true,
    controls: true,
    autoplay: false,
    mute: false,
    loop: false,
})

const classes = ref({
    figure: {
        _default: 'skew-with-shadow',
        size: 'w-full',
    },
    video: {
        object: 'object-cover object-center',
        size: 'w-full h-full',
    }
})

const videoEl:any = ref(null)
const muted = ref(props.mute)
const play = ref(props.autoplay)

const toggleMute = () => {
    muted.value = !muted.value
}

const togglePlay = () => {
    if (!videoEl.value) return
    play.value = !play.value

    if (play.value) {
        videoEl.value.play()
    } else {
        videoEl.value.pause()
    }
}

watch(
    () => props.reset,
    () => {
        if (props.reset) {
            if (!videoEl.value) return
            videoEl.value.pause()
            videoEl.value.currentTime = 0
        }
    }
)
</script>

<template>
    <div class="skew-container relative flex flex-col">
        <figure
            :aria-labelledby="video.title"
            :class="[getClasses(classes, 'figure'), utilities]"
        >
            <video
                ref="videoEl"
                width="1920"
                height="1080"
                :controls="controls"
                :autoplay="play"
                :muted="muted"
                :loop="loop"
                :class="[getClasses(classes, 'video')]"
                webkit-playsinline playsinline
            >
                <source
                    v-if="getIsMobile()"
                    :src="`${video.sd}#t=0.1`"
                    type="video/mp4"
                >
                <source
                    v-if="!getIsMobile()"
                    :src="`${video.hd}#t=0.1`"
                    type="video/mp4"
                >
                <slot></slot>
            </video>
        </figure>
        <div class="-mt-[10cqi] relative sm:absolute sm:bottom-[4.2cqi] left-0 w-full justify-end flex">
            <div class="bg-off-white-100 p-2 sm:p-4 rounded-full space-x-4 border-2 border-claret flex">
                <ButtonControls
                    v-if="muted"
                    icon="fas fa-volume-xmark"
                    type="button"
                    :label="getGlobalTranslation('unmute')"
                    utilities=""
                    @click="toggleMute()"
                />
                <ButtonControls
                    v-if="!muted"
                    icon="fas fa-volume"
                    type="button"
                    :label="getGlobalTranslation('mute')"
                    utilities=""
                    @click="toggleMute()"
                />
                <ButtonControls
                    v-if="play"
                    icon="fas fa-pause"
                    type="button"
                    :label="getGlobalTranslation('pause')"
                    utilities=""
                    @click="togglePlay()"
                />
                <ButtonControls
                    v-if="!play"
                    icon="fas fa-play"
                    type="button"
                    :label="getGlobalTranslation('play')"
                    utilities=""
                    @click="togglePlay()"
                />
            </div>
        </div>
    </div>
</template>